/**
 * Google Analytics 4.
 *
 * The gtag.js snippet itself is injected in gatsby-ssr.js, which defines
 * window.gtag and calls `config` with send_page_view disabled. Page views are
 * sent from gatsby-browser.js on route change instead, because Gatsby is a SPA
 * and gtag would otherwise only ever see the first page of a session.
 *
 * Everything here is a no-op when gtag is missing — no measurement ID
 * configured (local development), or a blocker has stopped the script.
 */

// Referrer details resolved at runtime by useReferrer, merged into every event
// so a partner's traffic can be segmented in reports. Module scope rather than
// React state: gatsby-browser.js needs to read it for page views too.
let referrerParams = {};

const gtag = (...args) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") {
    return;
  }

  window.gtag(...args);
};

// Drop empty values. GA4 stores an empty string as a real value, which shows up
// in reports as a blank row that is easily mistaken for missing data.
const compact = (params) => {
  const out = {};

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      out[key] = value;
    }
  });

  return out;
};

/**
 * Record which referrer the visitor arrived on.
 *
 * Sent two ways because they answer different questions:
 *   - user_properties are sticky for the session, so any GA4 report can be
 *     segmented by partner even for events fired before the lookup resolved.
 *   - referrerParams are merged into each event we send, so the referrer is
 *     available as an event-scoped dimension too (needed to attribute a
 *     specific enquiry rather than a whole session).
 *
 * @param {object} referrer A referrer from useReferrer.
 */
export const setReferrer = (referrer) => {
  // A referrer with no id is useReferrer's initial state: the site defaults,
  // whose sourceId means "no partner" (7 in production — see gatsby-config.js).
  // It must not be reported. useReferrer only learns the real referrer after a
  // re-render, so the defaults are what every first-render event would carry,
  // and form_view would land under a different source_id than the
  // generate_lead it precedes — breaking any funnel segmented by partner.
  // A visitor with no referrer genuinely has no source, so sending nothing is
  // both cheaper and more honest than sending 7.
  if (!referrer || !referrer.id) {
    return;
  }

  referrerParams = {
    ...referrerParams,
    // Merged over what is already known rather than replacing it, so a record
    // that happens to omit a field cannot blank one we resolved earlier.
    ...compact({
      referrer_id: referrer.id,
      referrer_name: referrer.name,
      referrer_context: referrer.context,
      introducer_id: referrer.introducerId,
      // GA4 coerces user properties to strings. Normalise here so the API's
      // string sourceId and siteMetadata's number cannot surface as two
      // distinct values for the same partner.
      source_id: referrer.sourceId && String(referrer.sourceId),
    }),
  };

  gtag("set", "user_properties", referrerParams);
};

/**
 * Record a referrer before its details have been looked up.
 *
 * The referrer API call in useReferrer can resolve after the visitor has
 * already started a form, so seed what we know from the ?ref query param on
 * client entry. A later setReferrer call fills in the rest of the record.
 *
 * Only reached when there is no stored referrer to seed from, since that
 * carries the id too — see onClientEntry in gatsby-browser.js.
 *
 * @param {string} referrerId The raw ref id from the query param.
 */
export const setReferrerId = (referrerId) => {
  if (!referrerId || referrerParams.referrer_id) {
    return;
  }

  referrerParams = { ...referrerParams, referrer_id: referrerId };
  gtag("set", "user_properties", referrerParams);
};

/**
 * Send a GA4 event.
 *
 * @param {string} name Event name. GA4 only accepts letters, numbers and
 *   underscores, so names are snake_case throughout.
 * @param {object} params Event parameters.
 */
export const trackEvent = (name, params) => {
  gtag("event", name, { ...referrerParams, ...compact(params) });
};

/**
 * Send a page view.
 *
 * GA4 derives its Page path dimension from page_location, so there is no
 * page_path parameter to send — that is a Universal Analytics habit, and an
 * extra parameter counts against the 25 allowed per event for nothing.
 *
 * page_location is built from the route rather than read off window.location so
 * it always describes the route being reported, independent of when this runs.
 *
 * @param {object} location A location with pathname and search.
 * @param {string} title Document title.
 */
export const trackPageView = ({ pathname, search, title }) => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  trackEvent("page_view", {
    page_location: `${origin}${pathname}${search || ""}`,
    page_title: title,
  });
};
